import React, { Component } from 'react';
import { TransactionData, TransactionJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import { ServiceNames } from '../../constant/ServiceNames';
import ServiceContainer from '../../config/ServiceContainer';
import RoleHelper from '../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import LanguageUtil from '../../util/LanguageUtil';
import AnimatedButton from 'components/custom/AnimatedButton';
import TimePickerContainer from '../custom/custom-time-picker/TimePickerContainer';
import { FormicaTimePickerDate } from '../custom/custom-time-picker/TimePickerDomain';
import { query, repository, subscribe } from '@trlogic/client-js';
import CaseService from 'service/CaseService';
import _ from 'lodash';
import NavigationUtil from 'util/NavigationUtil';
import Order = query.Order;
import QueryBuilder = query.QueryBuilder;
import SelectQuery = query.SelectQuery;
import Subscription = subscribe.Subscription;
import Computation = subscribe.Computation;
import MessageUtil from 'util/MessageUtil';
import SandboxTransactionListComponent from './SandboxTransactionListComponent';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from 'constant/Injectables';
import { RuleJsonModel } from 'domain/collection/rule/FlowDomains';
import WorkflowNodeUtil from 'util/WorkflowNodeUtil';
import FormicaMultiSelectBox from 'components/custom/FormicaMultiSelectBox';
import SandboxClient from 'client/sandbox/SandboxClient';
import GatewayContainer from 'config/GatewayContainer';
import GatewayNames from 'constant/GatewayNames';
import ValueUtil from 'util/ValueUtil';

class SandboxDatabaseContainer extends Component<Props, State> {
  public state = new State();

  private sandboxClient: SandboxClient = GatewayContainer.getInstance().get(GatewayNames.SANDBOX_CLIENT);
  private readonly transactionService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  protected itemSubscription: Subscription | undefined;
  protected computation: Computation | undefined;

  render() {
    const { selectedTransaction, selectedRules, transactionsList, totalRecord, rows, sort, currentPage, loading, startDate, endDate } = this.state;
    return (
      <div className={'flex flex-col w-full h-full gap-y-3'}>
        <div className={'flex items-center justify-between gap-x-5 gap-y-5'}>
          <div className={'flex gap-x-5 items-center justify-between w-full gap-y-2 flex-col lg:flex-row'}>
            <div className={'flex items-center w-full'}>
              <FormicaSearchSelectBox
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                rounded
                options={this.prepareTransactionSelectBox()}
                value={selectedTransaction?.name}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeSelectedTransaction(e.value)}
              />
            </div>
            <div className={'flex items-center w-full'}>
              <FormicaMultiSelectBox
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.rule', defaultMessage: 'Rule' })}
                rounded
                selectedItems={selectedRules}
                onChange={(items) => this.onChangeSelectedRules(items)}
                options={this.prepareTransactionRules()}
              />
            </div>
            <div className={'flex items-center w-full'}>
              <TimePickerContainer
                startDate={startDate}
                endDate={endDate}
                onChangeEndDate={(date) => this.setState({ endDate: date })}
                onChangeStartDate={(date) => this.setState({ startDate: date })}
              />
            </div>
            <div className={'flex items-center gap-x-5 w-full'}>
              <AnimatedButton
                onClick={this.searchFromElastic}
                disabled={!RoleHelper.canEdit('SandboxDatabase') || !selectedTransaction}
                icon={'search-1'}
                customSize={'sm'}
                iconSize={16}
                text={LanguageUtil.get({ id: 'common.button.search', defaultMessage: 'Search' })}
                customType={'secondary'}
              />
              <AnimatedButton
                onClick={this.sendTest}
                disabled={!RoleHelper.canEdit('SandboxDatabase') || !selectedTransaction || selectedRules.length === 0}
                customType='primary'
                iconSize={16}
                customSize={'sm'}
                icon={'deploy'}
                text={LanguageUtil.get({ id: 'common.button.test', defaultMessage: 'Test' })}
              />
            </div>
          </div>
        </div>

        <div className='flex gap-x-1'>
          <h5 className='text-f-black-3  text-md 2xl:text-lg select-none flex items-center'>
            {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
          </h5>
          <div className='total flex items-center cursor-pointer justify-center px-2 bg-[#F4F3FF] text-[#5925DC] h-[20px] min-w-[40px] rounded-xl'>
            {ValueUtil.numberShorten(totalRecord)}
          </div>
        </div>
        <SandboxTransactionListComponent
          transactions={transactionsList}
          total={totalRecord}
          rows={rows}
          loading={loading}
          sort={sort}
          paginationFixed
          onPageChange={(page: number, rows: number) => this.setState({ currentPage: page, rows: rows, loading: true }, this.searchFromElastic)}
          currentPage={currentPage}
          onSortChange={(order: Order[]) => this.setState({ sort: order, loading: true }, this.searchFromElastic)}
          multiple
          selectedTransaction={selectedTransaction}
        />
      </div>
    );
  }

  private searchFromElastic = async () => {
    const { startDate, endDate, selectedTransaction, rows } = this.state;
    if (!selectedTransaction) {
      MessageUtil.error({
        summary: 'Sandbox',
        detail: <div>{LanguageUtil.get({ id: 'messages.error.channelEmpty', defaultMessage: 'Channel cannot be empty!' })}</div>,
      });
      return false;
    }
    const start = new Date(startDate.value).valueOf();
    const end = new Date(endDate.value).valueOf();
    const query = SandboxDatabaseContainer.getTestQuery(selectedTransaction, rows, start, end);

    const { data, count } = await this.transactionService.getPageData(selectedTransaction?.name, query);
    this.setState({ transactionsList: data, totalRecord: count, loading: false });
  };

  private sendTest = async () => {
    const { startDate, endDate, selectedTransaction, selectedRules } = this.state;
    const from = new Date(startDate.value).valueOf();
    const to = new Date(endDate.value).valueOf();
    const showError = (messageId: string, defaultMessage: string) => {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.sandbox.historical.success.summary', defaultMessage: 'Test' }),
        detail: <div>{LanguageUtil.get({ id: messageId, defaultMessage })}</div>,
      });
      return false;
    };

    if (!selectedTransaction) {
      return showError('messages.error.channelEmpty', 'Channel cannot be empty!');
    }
    if (selectedRules.length === 0) {
      return showError('messages.error.ruleEmpty', 'Rule cannot be empty!');
    }

    try {
      const res = await this.sandboxClient.sendHistorical(
        selectedTransaction?.name,
        { from, to },
        selectedRules.map((rule) => rule.label),
      );
      if (res.success) {
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'pages.sandbox.historical.success.summary', defaultMessage: 'Test' }),
          detail: <div>{LanguageUtil.get({ id: 'pages.sandbox.historical.success.sendTest', defaultMessage: 'Test was sent successfully' })}</div>,
        });
      } else {
        showError('pages.sandbox.historical.errors.sendTest', 'An error occurred while sending test');
      }
    } catch (e) {
      showError('pages.sandbox.historical.errors.sendTest', 'An error occurred while sending test');
    }
  };

  public static getTestQuery(selectedTransaction: { name?: string } | undefined, rows: number, start: number, end: number): SelectQuery {
    let endpoint;
    if (selectedTransaction?.name) {
      endpoint = `risk_test_log_${NavigationUtil.getTenant()}_${selectedTransaction.name}_*`.toLowerCase();
    } else {
      endpoint = `risk_test_log_${NavigationUtil.getTenant()}_*`.toLowerCase();
    }

    const timeFilters: query.Filter[] = [query.gte('createdAt', start), query.lte('createdAt', end)];

    return QueryBuilder.select()
      .from(endpoint)
      .where(query.and(...timeFilters))
      .limit(rows)
      .build();
  }

  componentDidMount(): void {
    const itemQuery: query.SelectQueryBuilder = query.QueryBuilder.select().from('rules');
    this.itemSubscription = subscribe.Formica.subscribe('rules', itemQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.itemSubscription]);
  }

  protected tracker = async () => {
    if (this.itemSubscription?.ready()) {
      const itemQuery: query.Query = query.QueryBuilder.select().from('rules').build();
      const rulesList: RuleJsonModel[] = await this.repository.findMany(itemQuery);
      this.setState({ rulesList });
    }
  };

  protected filterRuleList = async (selectedTransaction: TransactionJsonModel) => {
    const { rulesList } = this.state;
    const filteredRules = rulesList.filter((rule) => {
      return WorkflowNodeUtil.getTransaction(rule.workflow)?.value?.name === selectedTransaction.name;
    });
    this.setState({ filteredRules });
  };

  private prepareTransactionSelectBox = () => {
    const { transactions } = this.props;
    return transactions.map((transaction: TransactionJsonModel) => ({
      label: transaction.name,
      value: transaction.name,
    }));
  };

  private onChangeSelectedTransaction = (name: string) => {
    const { transactions } = this.props;
    const selectedTransaction = transactions.find((transaction: TransactionJsonModel) => transaction.name === name);
    if (selectedTransaction) {
      this.setState({ selectedTransaction, selectedRules: [] });
      this.filterRuleList(selectedTransaction);
    }
  };

  private prepareTransactionRules = () => {
    const { filteredRules } = this.state;
    return (
      filteredRules?.map((rule: RuleJsonModel) => ({
        label: rule.name,
        value: rule.name,
      })) ?? []
    );
  };

  private onChangeSelectedRules(selectedRules: FormicaSelectBoxItem[]) {
    this.setState({ selectedRules });
  }
}

interface Props {
  transactions: TransactionJsonModel[];
}

class State {
  selectedTransaction: TransactionJsonModel | undefined = undefined;
  transactionsList: TransactionData[] = [];
  rulesList: RuleJsonModel[] = [];
  filteredRules: RuleJsonModel[] = [];
  selectedRules: FormicaSelectBoxItem[] = [];
  loading: boolean = false;
  totalRecord: number = 0;
  rows: number = 10;
  sort: Order[] = [];
  currentPage: number = 1;

  startDate: FormicaTimePickerDate = { value: new Date(), label: '' };
  endDate: FormicaTimePickerDate = { value: new Date(), label: '' };
}

export default SandboxDatabaseContainer;
